import React, { useState, useRef, useMemo, useEffect } from 'react';
import { DropdownProps } from 'semantic-ui-react';
import _ from 'lodash';
import { Link } from 'react-router-dom';
import { searchUsers, getRolesList } from '../../../Services/LoginServices';
import { AxiosResponse } from 'axios';
import { utils } from '../../../helpers/utils';
import AppConfig from '../../../Constants/config';
import moment from 'moment';

export interface PageSizeOption {
    key: number;
    value: string | number;
    text: string;
}

export interface GetUsersListPayload {
    sortByField: string | null;
    sortOrderDesc: boolean;
    searchText: string | null;
    recordsPerPage: number;
    pageNum: number;
    roleName: string;
}

export interface GetUsersListDataRow {
    [key: string]: any;
    name: string,
    userName: string,
    email: string,
    roleName: string,  
}

export interface GetUsersListData {
    totalRecords: number;
    gridData: GetUsersListDataRow[];
}

export interface MasterDataItem {
    name: string;
    id: number;
}

export interface MasterData {
    assignedToOptions: PageSizeOption[],
}

export interface MasterDataResponse {

}

export interface ColumnDefinition {
    columnName: string;
    sortable: boolean;
    columnHeader: string;
    decorator?: Function;
}

export function useUsersList(saveState: boolean = false, statePropName?: string) {
    

    const pageSizeOptions = useMemo<PageSizeOption[]>(() => {
        return [
            { key: 3, value: 3, text: '3' },
            { key: 5, value: 5, text: '5' },
            { key: 10, value: 10, text: '10' },
            { key: 15, value: 15, text: '15' },
            { key: 20, value: 20, text: '20' },
            { key: 50, value: 50, text: '50' },
            { key: 100, value: 100, text: '100' }
        ];
    }, []);

    const searchRef = useRef<HTMLInputElement>(null);
    const [masterData, setMasterData] = useState<MasterDataItem[]>([]);
    const [recordsPerPage, setRecordsPerPage] = useState<number>(10);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [pageNum, setPageNum] = useState<number>(1);
    const [sortByField, setSortByField] = useState<string | null>('name');
    const [sortOrderDesc, setSortOrderDesc] = useState<boolean>(false);
    const [searchText, setSearchText] = useState<string | null>('');
    const [data, setData] = useState<GetUsersListDataRow[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [roleName, setRoleName] = useState<string>('Any');

    const handlePageSizeChange: ((event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => void) | undefined = (event, data) => {
        setRecordsPerPage(data.value as number);
        const payload: GetUsersListPayload = {
            recordsPerPage: data.value as number,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: searchText,
            pageNum: pageNum,
            roleName: roleName,
        };
        _fetchData(payload);
    }

    const handlePaginationChange: (newPageNumber: number) => void = (newPageNumber) => {
        setPageNum(newPageNumber);
        const payload: GetUsersListPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: searchText,
            pageNum: newPageNumber,
            roleName: roleName,
        };
        _fetchData(payload);
    }

    const handleRoleNameChange: (newValue: string) => void = (newValue) => {
        setRoleName(newValue);
        setPageNum(1);
        const payload: GetUsersListPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: searchText,
            pageNum: 1,
            roleName: newValue,
        };
        _fetchData(payload);
    }

    const handleSortChange: (newSortColumnName: string) => void = (newSortColumnName) => {
        let newIsDesc = sortOrderDesc;
        let newColumnName = sortByField;
        if (newSortColumnName === sortByField) {
            newIsDesc = !newIsDesc;
        } else {
            newColumnName = newSortColumnName;
        }

        setSortOrderDesc(newIsDesc);
        setSortByField(newColumnName);
        setPageNum(1);
        const payload: GetUsersListPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: newColumnName,
            sortOrderDesc: newIsDesc,
            searchText: searchText,
            pageNum: 1,
            roleName: roleName,
        };
        _fetchData(payload);
    }

    const handleSearchChangeFn: (event: React.SyntheticEvent<HTMLElement, Event>) => void = (event) => {
        //setSearchText((event.target as HTMLInputElement).value);
        setSearchText((event.target as HTMLInputElement).value);
        setPageNum(1);
        const payload: GetUsersListPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: (event.target as HTMLInputElement).value,
            pageNum: 1,
            roleName: roleName,
        };
        _fetchData(payload);
    }
    const handleSearchChange = _.debounce(handleSearchChangeFn, 1000);

    const resetSearch: (event: React.SyntheticEvent<HTMLElement, Event>) => void = (event) => {
        if (searchRef.current !== null) {
            (searchRef.current as HTMLInputElement).value = '';
        }
        setSearchText(null);
        setPageNum(1);
        const payload: GetUsersListPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: null,
            pageNum: 1,
            roleName: roleName,
        };
        _fetchData(payload);
    }

    const resetFilter: (event: React.SyntheticEvent<HTMLElement, Event>) => void = (event) => {
        setSearchText(null);
        if(searchRef.current){
            (searchRef.current as HTMLInputElement).value = '';
        }
        setPageNum(1);
        const payload: GetUsersListPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: null,
            pageNum: 1,
            roleName: roleName,
        };
        _fetchData(payload);
    }


    const _fetchData: (overwritePayload?: GetUsersListPayload) => void = (overwritePayload) => {
        const defaultPayload: GetUsersListPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: searchText,
            pageNum: pageNum,
            roleName: roleName,
        };

        let payload: GetUsersListPayload = defaultPayload;
        if (overwritePayload !== undefined) {
            payload = overwritePayload;
        }
        if(payload.roleName === 'Any'){
            payload.roleName = '';
        }
        //console.log(payload);
        setIsLoading(true);
        if (saveState) {
            _saveState(payload);
        }
        searchUsers(payload, false).then((result) => {
            setIsLoading(false);

            let response: AxiosResponse<unknown, GetUsersListData> = result as AxiosResponse;
            if (response.status === 200 && response.data) {

                let data = response.data as GetUsersListData;
                setData(data.gridData);
                setTotalRecords(data.totalRecords);
            }
        }, (error) => {
            setIsLoading(false);
            console.log(error);
        });
    }

    const _fetchMasterData: (callback?: Function) => void = (callBack = () => { }) => {
        setIsLoading(true);
        getRolesList(false).then((result) => {
            setIsLoading(false);
            let response: AxiosResponse<unknown, MasterDataResponse> = result as AxiosResponse;
            if (response && response.status === 200 && response.data) {
                let data = response.data as MasterDataItem[];
                data.unshift({
                    id: 0,
                    name: 'Any'
                });
                setMasterData(data);
                callBack();
            }
        }, (error) => {
            setIsLoading(false);
            console.log(error);
        });
    }

    const _saveState = (currentPayload: GetUsersListPayload) => {
        let stateName = statePropName || AppConfig.USER_LIST_STATE;
        if (saveState) {
            utils.updateInLocalStorage(stateName, JSON.stringify(currentPayload));
        }
    }

    const _loadState = () => {
        let stateName = statePropName || AppConfig.USER_LIST_STATE;
        let stateString: string | null = utils.getLocalStorageItem(stateName);
        if (stateString && saveState) {
            return JSON.parse(stateString) as GetUsersListPayload;
        } else {
            return null;
        }

    }

    useEffect(() => {
        _fetchMasterData(() => {
            let priviousState: GetUsersListPayload | null = _loadState();
            if (priviousState && saveState) {
                _fetchData(priviousState);
                setSortByField(priviousState.sortByField);
                setSortOrderDesc(priviousState.sortOrderDesc);
                setSearchText(priviousState.searchText);
                setRecordsPerPage(priviousState.recordsPerPage);
                setRoleName(priviousState.roleName);
                setPageNum(priviousState.pageNum);
                if (searchRef.current) {
                    searchRef.current.value = priviousState.searchText || '';
                }
                
            } else {
                _fetchData();
            }
        });

    }, []);

    // column definition

    let columns: ColumnDefinition[];
    columns = [
        {
            columnName: 'name',
            sortable: true,
            columnHeader: 'Name',
            decorator: (row: GetUsersListDataRow, data: GetUsersListDataRow[], rowIndex: number, colIndex: number ) => {
                return (<Link to={`/Edituser/${row.userId}`}>{row.name}</Link>);
            },
        },
        {
            columnName: 'username',
            sortable: true,
            columnHeader: 'Login',
            decorator: (row: GetUsersListDataRow, data: GetUsersListDataRow[], rowIndex: number, colIndex: number ) => {
                return (<span>{row.userName}</span>);
            },
        },
        {
            columnName: 'email',
            sortable: false,
            columnHeader: 'Email',
            decorator: (row: GetUsersListDataRow, data: GetUsersListDataRow[], rowIndex: number, colIndex: number ) => {
                return (<span>{row.email}</span>);
            },
        },
        {
            columnName: 'role',
            sortable: false,
            columnHeader: 'Role',
            decorator: (row: GetUsersListDataRow, data: GetUsersListDataRow[], rowIndex: number, colIndex: number ) => {
                return (<span>{row.roleName}</span>);
            },
        },
    ];

    return [columns, recordsPerPage, totalRecords, pageNum,
        pageSizeOptions, searchRef, searchText, data, isLoading, sortOrderDesc, sortByField,
        roleName, masterData,
        handlePageSizeChange, handleSearchChange, resetSearch, handlePaginationChange, handleSortChange, handleRoleNameChange,
    ] as const;
}